import { gql } from '@apollo/client';

export const LOGIN_USER = gql`
  mutation LoginUser(
    $email: String!
    $password: String!
    $send2fa: Boolean
    $loginDateToken: String
  ) {
    login(
      email: $email
      password: $password
      send2fa: $send2fa
      loginDateToken: $loginDateToken
    ) {
      token
      loggedIn
      verified
      phoneNumber
      countryCode
      method
    }
  }
`;

export const LOGOUT_USER = gql`
  mutation LogoutUser {
    logout
  }
`;

export const UPDATE_CURRENT_USER_PROFILE = gql`
  mutation UpdateCurrentUserProfile($givenname: String!, $surname: String!) {
    updateCurrentUserProfile(givenname: $givenname, surname: $surname) {
      _id
      emails {
        address
      }
      profile {
        givenname
        surname
        school {
          _id
          name
        }
      }
      roles
      twoFactor {
        method
      }
    }
  }
`;

export const UPDATE_ACCOUNT_DETAILS = gql`
  mutation UpdateAccountDetails(
    $_id: String!
    $givenname: String!
    $surname: String!
    $email: String!
    $providerid: String
    $lang: String!
    $roles: [String]
  ) {
    updateAccountDetails(
      _id: $_id
      givenname: $givenname
      surname: $surname
      email: $email
      providerid: $providerid
      lang: $lang
      roles: $roles
    ) {
      _id
      emails {
        address
      }
      profile {
        givenname
        surname
        lang
      }
      roles
    }
  }
`;

export const ADD_SCHOOLS_TO_USER = gql`
  mutation AddSchoolsToUser($userId: String!, $schoolIds: [String]!) {
    addSchoolsToUser(userId: $userId, schoolIds: $schoolIds) {
      _id
      schoolPermissions {
        school {
          _id
          name
        }
        insightsPermissionIds {
          key
          label {
            en
            fr
          }
          category
        }
        availableProductsAndPermissions {
          name
          permissions {
            _id
            key
            label {
              en
              fr
            }
            category
          }
        }
        dataFilterSummary {
          product
          columns
          rows
        }
      }
    }
  }
`;

export const REMOVE_SCHOOLS_FROM_USER = gql`
  mutation RemoveSchoolsFromUser($userId: String!, $schoolIds: [String]!) {
    removeSchoolsFromUser(userId: $userId, schoolIds: $schoolIds) {
      _id
      schoolPermissions {
        school {
          _id
          name
        }
        insightsPermissionIds {
          key
          label {
            en
            fr
          }
          category
        }
        availableProductsAndPermissions {
          name
          permissions {
            _id
            key
            label {
              en
              fr
            }
            category
          }
        }
        dataFilterSummary {
          product
          columns
          rows
        }
      }
    }
  }
`;

export const UPDATE_PERMISSIONS = gql`
  mutation UpdatePermissions(
    $userId: String!
    $schoolId: String!
    $permissions: [EditedPermissions!]!
    $granularPermissions: GranularPermissions
    $isNoAccess: Boolean
  ) {
    updatePermissions(
      userId: $userId
      schoolId: $schoolId
      permissions: $permissions
      granularPermissions: $granularPermissions
      isNoAccess: $isNoAccess
    ) {
      user {
        _id
        schoolPermissions {
          school {
            _id
            name
          }
          insightsPermissionIds {
            key
            label {
              en
              fr
            }
            category
          }
          availableProductsAndPermissions {
            name
            permissions {
              _id
              key
              label {
                en
                fr
              }
              category
            }
          }
          dataFilterSummary {
            product
            columns
            rows
          }
        }
      }
      isReloadRequired
    }
  }
`;

export const CREATE_ADMIN = gql`
  mutation CreateAdmin(
    $givenname: String!
    $surname: String!
    $email: String!
    $lang: String!
  ) {
    createAdmin(
      givenname: $givenname
      surname: $surname
      email: $email
      lang: $lang
    ) {
      _id
      emails {
        address
      }
      profile {
        name
        givenname
        surname
        schools {
          _id
          name
        }
        lang
      }
      roles
      schoolPermissions {
        school {
          _id
          name
        }
        insightsPermissionIds {
          key
          label {
            en
            fr
          }
          category
        }
        availableProductsAndPermissions {
          name
          permissions {
            _id
            key
            label {
              en
              fr
            }
            category
          }
        }
        dataFilterSummary {
          product
          columns
          rows
        }
      }
      createdAt
      twoFactor {
        method
      }
      acceptanceHistory {
        dateAccepted
        checked
      }
    }
  }
`;

export const CREATE_USER = gql`
  mutation CreateUser(
    $givenname: String!
    $surname: String!
    $email: String!
    $lang: String!
    $schoolIds: [String!]!
    $providerid: String
    $createAnalyticsAccount: Boolean
  ) {
    createUser(
      givenname: $givenname
      surname: $surname
      email: $email
      lang: $lang
      schoolIds: $schoolIds
      providerid: $providerid
      createAnalyticsAccount: $createAnalyticsAccount
    ) {
      _id
      emails {
        address
      }
      profile {
        name
        givenname
        surname
        schools {
          _id
          name
        }
        lang
      }
      roles
      schoolPermissions {
        school {
          _id
          name
        }
        insightsPermissionIds {
          key
          label {
            en
            fr
          }
          category
        }
        availableProductsAndPermissions {
          name
          permissions {
            _id
            key
            label {
              en
              fr
            }
            category
          }
        }
        dataFilterSummary {
          product
          columns
          rows
        }
      }
      createdAt
      twoFactor {
        method
      }
      acceptanceHistory {
        dateAccepted
        checked
      }
    }
  }
`;

export const UPDATE_USER = gql`
  mutation UpdateUser(
    $_id: String!
    $email: String
    $schoolPermissions: [UserSchoolInput]
    $unselectedPermissions: [UserSchoolInput]
    $role: String
  ) {
    updateUser(
      _id: $_id
      email: $email
      schoolPermissions: $schoolPermissions
      unselectedPermissions: $unselectedPermissions
      role: $role
    ) {
      _id
      emails {
        address
      }
      profile {
        givenname
        surname
        school {
          _id
          name
        }
      }
      roles
      twoFactor {
        method
      }
    }
  }
`;

export const UPDATE_USER_CREDENTIALS = gql`
  mutation ActivateUser(
    $name: String!
    $email: String!
    $givenname: String!
    $surname: String!
    $password: String!
    $token: String!
    $lang: String!
  ) {
    activateUser(
      name: $name
      email: $email
      givenname: $givenname
      surname: $surname
      password: $password
      token: $token
      lang: $lang
    ) {
      emails {
        address
      }
    }
  }
`;

export const REMOVE_USER = gql`
  mutation DeleteUser($_id: String!) {
    deleteUser(_id: $_id) {
      emails {
        address
      }
    }
  }
`;

export const CHECK_2FA = gql`
  mutation Check2fa(
    $code: String!
    $persist: Boolean
    $token: String
    $login: Boolean
  ) {
    check2fa(code: $code, persist: $persist, token: $token, login: $login)
  }
`;

export const RESEND_2FA = gql`
  mutation Resend2fa(
    $token: String!
    $isUpdate: Boolean
    $overrideMethod: String
  ) {
    resend2fa(
      token: $token
      isUpdate: $isUpdate
      overrideMethod: $overrideMethod
    )
  }
`;

export const RESEND_2FA_BY_EMAIL = gql`
  mutation Resend2faByEmail($token: String) {
    resend2faByEmail(token: $token)
  }
`;

export const SEND_2FA_BY_EMAIL = gql`
  mutation Send2faByEmail($token: String) {
    send2faByEmail(token: $token)
  }
`;

export const SEND_USER_INVITE_EMAIL = gql`
  mutation sendUserInviteEmail($_id: String!) {
    sendUserInviteEmail(_id: $_id) {
      result
      message
    }
  }
`;

export const ACTIVATE_2FA = gql`
  mutation Activate2fa(
    $token: String!
    $countryCode: String!
    $phoneNumber: String!
    $phoneExtension: String!
    $method: String!
    $preActivate: Boolean
  ) {
    activate2fa(
      token: $token
      countryCode: $countryCode
      phoneNumber: $phoneNumber
      phoneExtension: $phoneExtension
      method: $method
      preActivate: $preActivate
    ) {
      emails {
        address
      }
    }
  }
`;

export const SEND_RESET_PASSWORD = gql`
  mutation SendResetPassword($email: String!) {
    sendResetPassword(email: $email)
  }
`;

export const RESET_PASSWORD = gql`
  mutation ResetPassword($token: String!, $password: String!) {
    resetPassword(token: $token, password: $password) {
      emails {
        address
      }
    }
  }
`;

export const UPDATE_PASSWORD = gql`
  mutation ResetPassword($currentPassword: String!, $password: String!) {
    resetPassword(currentPassword: $currentPassword, password: $password) {
      emails {
        address
      }
    }
  }
`;

export const CONFIRM_PASSWORD = gql`
  mutation ConfirmPassword($password: String!) {
    confirmPassword(password: $password) {
      emails {
        address
      }
    }
  }
`;

export const CHECK_TERMS = gql`
  mutation CheckTerms($token: String!, $checked: Boolean) {
    checkTerms(token: $token, checked: $checked) {
      _id
    }
  }
`;

export const UPDATE_FEATURE_SWITCH = gql`
  mutation UpdateFeatureSwitch($featureName: String!, $value: Boolean) {
    switchUserFeature(featureName: $featureName, value: $value) {
      featureSwitches {
        name
        value
      }
    }
  }
`;

export const UPDATE_LANGUAGE = gql`
  mutation UpdateLanguage($lang: String!) {
    updateLanguage(lang: $lang) {
      profile {
        lang
      }
    }
  }
`;

export const CHECK_EMAIL_AND_PERMISSIONS = gql`
  mutation CheckEmailAndPermissions(
    $email: String!
    $role: String
    $schoolIds: [String]
    $force: Boolean
  ) {
    checkEmailAndPermissions(
      email: $email
      role: $role
      schoolIds: $schoolIds
      force: $force
    ) {
      schoolId
      general {
        _id
        key
        label {
          en
          fr
        }
        selected
        disabled
      }
      snapshot {
        _id
        key
        label {
          en
          fr
        }
        selected
        disabled
      }
      duet {
        _id
        key
        label {
          en
          fr
        }
        selected
        disabled
      }
    }
  }
`;

// UserSchoolInput is an input type defined on the API
export const INVITE_USER = gql`
  mutation InviteUser(
    $email: String!
    $role: String!
    $schoolPermissions: [UserSchoolInput]
    $force: Boolean
  ) {
    inviteUser(
      email: $email
      role: $role
      schoolPermissions: $schoolPermissions
      force: $force
    ) {
      _id
    }
  }
`;

export const DELETE_TEMP_2FA = gql`
  mutation DeleteTemp2Fa {
    deleteTemp2Fa {
      success
    }
  }
`;

export const SYNC_PERMISSIONS = gql`
  mutation SyncPermissions($userId: String!) {
    syncPermissions(userId: $userId) {
      _id
      schoolPermissions {
        school {
          _id
          name
        }
        insightsPermissionIds {
          key
          label {
            en
            fr
          }
          category
        }
        availableProductsAndPermissions {
          name
          permissions {
            _id
            key
            label {
              en
              fr
            }
            category
          }
        }
        dataFilterSummary {
          product
          columns
          rows
        }
      }
    }
  }
`;
